import type { NoteList, NoteSave, NoteSaved, RecordNote, RecordNoteSummary } from "@/domain/note";
import { InvalidationEntity } from "../entityDependencies";
import { queryKeys } from "../queryKeys";
import { detailSlot, isObject, listSlot, type PageShape } from "../cache/cacheSlots";
import { defineEntityCache } from "../cache/entityCache";

export type NotePatch = Partial<Omit<RecordNote, "uuid" | "holder">>;

const page: PageShape<NoteList, RecordNoteSummary, string> = {
  isPage: (value): value is NoteList =>
    isObject(value) && Array.isArray(value.items) && "next_cursor" in value,
  records: (current) => current.items,
  withRecords: (current, items) => ({ ...current, items }),
  idOf: (note) => note.uuid,
};

function isRecordNote(value: unknown): value is RecordNote {
  return isObject(value) && typeof value.uuid === "string" && isObject(value.body);
}

export const noteCache = defineEntityCache<string, NotePatch>({
  entity: InvalidationEntity.NOTE,
  slots: (orgId) => [
    listSlot({
      prefix: queryKeys.notes.lists(orgId),
      shape: page,
      merge: (note, { body: _body, ...patch }) => ({ ...note, ...patch }),
    }),
    detailSlot({
      prefix: queryKeys.notes.details(orgId),
      isRecord: isRecordNote,
      idOf: (note) => note.uuid,
      merge: (note, patch) => ({ ...note, ...patch }),
    }),
  ],
  records: (orgId, uuid) => [queryKeys.notes.detail(orgId, uuid)],
});

/** What a save changes on the note: the edited fields plus the server's new revision stamp. */
export const savedNote = (saved: NoteSaved, changes: NoteSave): NotePatch => ({
  ...(changes.title !== undefined ? { title: changes.title } : {}),
  ...(changes.body !== undefined ? { body: changes.body } : {}),
  excerpt: saved.excerpt,
  revision: saved.revision,
  updated_at: saved.updated_at,
  last_edited_by: saved.last_edited_by,
});
